'use client';

import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { CheckoutAddressTypeEnum } from '@sola/schema/dotcom/checkout/contactInfo.js';
import {
  BillingAddressEnum,
  MailingAddressEnum,
} from '@sola/schema/dotcom/shared/address.js';
import {
  COUNTRY_CODE_US,
  CountryCode,
  CountryEnum,
  StateEnum,
} from '@sola/schema/dotcom/shared/geo.js';

import { InputField } from '../input-field/input-field.js';
import { PostalCodeField } from '../postal-code-field/postal-code-field.js';
import { Select } from '../select/select.js';
import { StateField } from '../state-field/state-field.js';

const stateOptions = Object.entries(StateEnum).map(([abbr, name]) => ({
  text: name,
  value: abbr,
}));
const countryOptions = Object.entries(CountryEnum).map(([abbr, name]) => ({
  text: name,
  value: abbr,
}));

export interface AddressFieldsProps {
  addressType: CheckoutAddressTypeEnum;
  /**
   * Used only when country needs to be controlled.
   */
  countryValue?: CountryCode;
  defaultCountry?: CountryCode;
  onCountryChange?: (countryCode: CountryCode) => void;
  shouldUnregister?: boolean;
  shouldValidate?: boolean;
}

export const AddressFields: React.FC<AddressFieldsProps> = ({
  addressType,
  countryValue,
  defaultCountry,
  onCountryChange,
  shouldUnregister,
  shouldValidate = true,
}) => {
  const { t } = useTranslation(['checkout']);
  const [countryState, setCountryState] = useState(defaultCountry);
  const selectedCountry = countryValue || countryState;
  const addressLabelToken = `${addressType}AddressLabel`;
  const autoCompleteGroup =
    addressType === CheckoutAddressTypeEnum.Mailing ? '' : 'billing '; // The space after billing is NECESSARY - it denotes an autocomplete group
  const FieldGroupEnum =
    addressType === CheckoutAddressTypeEnum.Mailing
      ? MailingAddressEnum
      : BillingAddressEnum;
  const isUS = selectedCountry === COUNTRY_CODE_US;

  return (
    <>
      <InputField
        label={t(`checkout:form.addressFields.${addressLabelToken}`)}
        name={FieldGroupEnum.Address}
        autoComplete={`${autoCompleteGroup}address-line1`}
        required={shouldValidate}
        removeCardNumbers={{
          message: t('checkout:form.addressFields.addressNumberError'),
        }}
        pattern={{
          value:
            /^$|^(?![\w!#$%&'*+/=?^_`{|}~.-]+@[a-zA-Z0-9-]+(\.[a-zA-Z0-9-]+)*\.([a-zA-Z]{2,63}))/,
          message: t('checkout:form.addressFields.addressNumberError'),
        }}
        shouldUnregister={shouldUnregister ? true : undefined}
      />

      <InputField
        label={t('checkout:form.addressFields.addressAdditionalLabel')}
        name={FieldGroupEnum.Address2}
        autoComplete={`${autoCompleteGroup}address-line2`}
        removeCardNumbers={{
          message: t('checkout:form.addressFields.addressNumberError'),
        }}
        shouldUnregister={shouldUnregister ? true : undefined}
      />
      <Select
        label={t('checkout:form.addressFields.countryLabel')}
        placeholder={t('checkout:form.addressFields.countryPlaceholder')}
        defaultValue={defaultCountry}
        name={FieldGroupEnum.Country}
        autoComplete={`${autoCompleteGroup}country`}
        required={shouldValidate}
        items={countryOptions}
        shouldUnregister={shouldUnregister ? true : undefined}
        onChange={(e) => {
          const { value } = e.target;
          if (onCountryChange) {
            onCountryChange(value);
          } else {
            setCountryState(value);
          }
        }}
        {...(countryValue && { value: countryValue })}
      />

      <div className="flex gap-comfy-sm flex-col lg:flex-row">
        <InputField
          label={t('checkout:form.addressFields.cityLabel')}
          name={FieldGroupEnum.City}
          fieldClasses="flex-1"
          autoComplete={`${autoCompleteGroup}address-level2`}
          autoCorrect="off"
          spellCheck={false}
          required={shouldValidate}
          removeCardNumbers={{
            message: t('checkout:form.addressFields.cityNumberError'),
          }}
          shouldUnregister={shouldUnregister ? true : undefined}
        />

        <StateField
          label={
            isUS
              ? t('checkout:form.addressFields.stateLabel')
              : t('checkout:form.addressFields.provinceLabel')
          }
          name={FieldGroupEnum.State}
          fieldClasses="flex-1"
          autoComplete={`${autoCompleteGroup}address-level1`}
          spellCheck={false}
          removeCardNumbers={{
            message: t('checkout:form.addressFields.provinceNumberError'),
          }}
          placeholder={
            isUS ? t('checkout:form.addressFields.statePlaceholder') : ''
          }
          items={stateOptions}
          required={shouldValidate && isUS}
          shouldUnregister={shouldUnregister ? true : undefined}
          isUS={isUS}
        />

        <PostalCodeField
          label={
            isUS
              ? t('checkout:form.addressFields.zipCodeLabel')
              : t('checkout:form.addressFields.postalCodeLabel')
          }
          name={FieldGroupEnum.Postal}
          fieldClasses="flex-1"
          autoComplete={`${autoCompleteGroup}postal-code`}
          shouldUnregister={shouldUnregister ? true : undefined}
          countryCode={selectedCountry}
          shouldValidate={shouldValidate}
        />
      </div>
    </>
  );
};
